/*----------------------------------
- RECRUITMENT API DOCS CONTENT
----------------------------------*/

/*----------------------------------
- TYPES
----------------------------------*/

export type RecruitmentApiDocJsonPrimitive = string | number | boolean | null;
export type RecruitmentApiDocJsonValue =
    | RecruitmentApiDocJsonPrimitive
    | RecruitmentApiDocJsonObject
    | RecruitmentApiDocJsonValue[];

export type RecruitmentApiDocJsonObject = {
    [key: string]: RecruitmentApiDocJsonValue;
};

export type RecruitmentApiDocCodeLanguage = 'curl' | 'javascript';
export type RecruitmentApiDocPreviewMode = 'example' | 'typings';
export type RecruitmentApiDocFieldLocation = 'header' | 'body' | 'path' | 'response';
export type RecruitmentApiDocSectionId = 'overview' | 'companies' | 'create-mission' | 'list-missions' | 'update-mission';

export type RecruitmentApiDocHighlight = {
    label: string;
    value: string;
};

export type RecruitmentApiDocField = {
    name: string;
    type: string;
    required: boolean;
    location: RecruitmentApiDocFieldLocation;
    description: string;
};

export type RecruitmentApiDocStep = {
    title: string;
    description: string;
};

export type RecruitmentApiDocEndpoint = {
    method: string;
    path: string;
    description: string;
};

export type RecruitmentApiDocSection = {
    id: RecruitmentApiDocSectionId;
    label: string;
    eyebrow: string;
    title: string;
    summary: string;
    description: string;
    bullets: readonly string[];
    method?: string;
    path?: string;
    requestFields: readonly RecruitmentApiDocField[];
    responseFields: readonly RecruitmentApiDocField[];
};

export type RecruitmentApiDocLanguageOption = {
    id: RecruitmentApiDocCodeLanguage;
    label: string;
};

export type RecruitmentApiDocPreviewOption = {
    id: RecruitmentApiDocPreviewMode;
    label: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

export const RECRUITMENT_API_DOCS_CANONICAL_PATH = '/api/recruitment';
export const RECRUITMENT_API_DOCS_LANGUAGES: readonly RecruitmentApiDocLanguageOption[] = [
    { id: 'curl', label: 'cURL' },
    { id: 'javascript', label: 'JavaScript' },
] as const;

export const RECRUITMENT_API_DOCS_PREVIEW_MODES: readonly RecruitmentApiDocPreviewOption[] = [
    { id: 'example', label: 'JSON examples' },
    { id: 'typings', label: 'TypeScript typings' },
] as const;

const RECRUITMENT_API_KEY_EXAMPLE = 'Recruitment-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx';
const RECRUITMENT_API_LEGACY_BASE_PATH = '/partners/hunteron/v1';
const RECRUITMENT_API_COMPANIES_PATH = '/partners/v1/companies';
const RECRUITMENT_API_MISSIONS_PATH = '/partners/v1/missions';
const RECRUITMENT_API_MISSION_UPDATE_PATH = '/partners/v1/missions/:missionId';
const RECRUITMENT_API_MISSION_UPDATE_EXAMPLE_ID = 'rec_01JZ4J6H2T8Y1R3P5N7K9M1Q2W';
const RECRUITMENT_API_MISSION_UPDATE_EXAMPLE_PATH = `${RECRUITMENT_API_MISSIONS_PATH}/${RECRUITMENT_API_MISSION_UPDATE_EXAMPLE_ID}`;

const RECRUITMENT_API_COMPANY_REQUEST_EXAMPLE: RecruitmentApiDocJsonObject = {
    name: 'Acme Robotics',
    companySize: '51-200',
    websiteUrl: 'https://acme-robotics.example.com',
    sectors: ['software', 'robotics'],
    presentation: 'Acme Robotics builds automation systems for industrial operators.',
    valuesPresentation: 'Pragmatic engineering, ownership, and customer clarity.',
    productPresentation: 'A fleet orchestration platform for autonomous warehouse systems.',
};

const RECRUITMENT_API_MISSION_REQUEST_EXAMPLE: RecruitmentApiDocJsonObject = {
    company: 'acme-robotics',
    title: 'Senior Backend Engineer',
    seatsCount: 2,
    position: 'backend-engineer',
    locationType: 'Hybrid',
    locations: ['france'],
    locationCity: 'Paris',
    languages: ['en', 'fr'],
    jobType: 'CDI',
    positionExpMin: 4,
    positionExpMax: 8,
    sectorExpMin: 2,
    sectorExpMax: 6,
    teamPresentation: 'Small platform team working with product and robotics engineers.',
    jobPresentation: 'Own API services that coordinate warehouse automation workflows.',
    requirementsDescription: 'Strong TypeScript, distributed systems, and production API experience.',
    hardSkills: 'TypeScript, Node.js, PostgreSQL, API design',
    softSkills: 'Clear communication and product judgment',
    possibleEvolutionsDescription: 'Lead the platform backend scope as the team grows.',
    employeeAdvantages: 'Hybrid work, hardware lab access, and learning budget.',
    customQuestions: 'Which API reliability problem are you most proud of solving?',
    forbiddenHeadhunting: 'Do not contact current Acme Robotics customers.',
    recruitmentProcess: 'Screening call, technical interview, leadership interview.',
    jobStart: 'ASAP',
    salaryMin: 70000,
    salaryMax: 90000,
    salaryCurrency: '€',
    trialPeriod: 3,
    paymentSchedule: 'Success fee after signed contract.',
    notesFromCsm: 'Prioritize candidates with logistics or robotics experience.',
};

const RECRUITMENT_API_MISSION_UPDATE_EXAMPLE: RecruitmentApiDocJsonObject = {
    status: 'Paused',
    title: 'Senior Platform Engineer',
    seatsCount: 1,
    locations: ['france', 'belgium'],
    locationCity: 'Paris or Brussels',
    languages: ['en'],
    salaryCurrency: '€',
    notesFromCsm: 'Role paused while the hiring team reviews the shortlist.',
};

const RECRUITMENT_API_COMPANY_REQUEST_TYPINGS = [
    'type CreateCompanyRequest = {',
    '    name: string;',
    '    companySize?: string;',
    '    websiteUrl?: string;',
    '    sectors?: string[];',
    '    presentation: string;',
    '    valuesPresentation?: string;',
    '    productPresentation?: string;',
    '};',
].join('\n');

const RECRUITMENT_API_COMPANY_RESPONSE_TYPINGS = [
    'type CreateCompanyResponse = {',
    '    companyId: string;',
    '};',
    '',
    'type Company = {',
    '    airtableId: string;',
    '    name: string;',
    '    companySize: string | null;',
    '    websiteUrl: string | null;',
    '    sectors: string[];',
    '    presentation: string | null;',
    '    valuesPresentation: string | null;',
    '    productPresentation: string | null;',
    '};',
    '',
    'type ListCompaniesResponse = Company[];',
].join('\n');

const RECRUITMENT_API_MISSION_CREATE_REQUEST_TYPINGS = [
    'type CreateMissionRequest = {',
    '    company: string;',
    '    title: string;',
    '    seatsCount: number;',
    '    jobPresentation: string;',
    '    position?: string;',
    '    locationType?: string;',
    '    locations?: string[];',
    '    locationCity?: string;',
    '    languages?: string[];',
    '    jobType?: string;',
    '    positionExpMin?: number;',
    '    positionExpMax?: number;',
    '    sectorExpMin?: number;',
    '    sectorExpMax?: number;',
    '    teamPresentation?: string;',
    '    requirementsDescription?: string;',
    '    hardSkills?: string;',
    '    softSkills?: string;',
    '    possibleEvolutionsDescription?: string;',
    '    employeeAdvantages?: string;',
    '    customQuestions?: string;',
    '    forbiddenHeadhunting?: string;',
    '    recruitmentProcess?: string;',
    '    jobStart?: string;',
    '    salaryMin?: number;',
    '    salaryMax?: number;',
    "    salaryCurrency?: '€' | '$' | '£' | '¥';",
    '    trialPeriod?: number;',
    '    paymentSchedule?: string;',
    '    notesFromCsm?: string;',
    '};',
].join('\n');

const RECRUITMENT_API_MISSION_UPDATE_REQUEST_TYPINGS = [
    'type UpdateMissionRequest = {',
    "    status?: 'Active' | 'Paused' | 'Closed by CP' | 'Closed by Client';",
    '    company?: string;',
    '    title?: string;',
    '    seatsCount?: number;',
    '    position?: string;',
    '    locationType?: string;',
    '    locations?: string[];',
    '    locationCity?: string;',
    '    languages?: string[];',
    '    jobType?: string;',
    '    positionExpMin?: number;',
    '    positionExpMax?: number;',
    '    sectorExpMin?: number;',
    '    sectorExpMax?: number;',
    '    teamPresentation?: string;',
    '    jobPresentation?: string;',
    '    requirementsDescription?: string;',
    '    hardSkills?: string;',
    '    softSkills?: string;',
    '    possibleEvolutionsDescription?: string;',
    '    employeeAdvantages?: string;',
    '    customQuestions?: string;',
    '    forbiddenHeadhunting?: string;',
    '    recruitmentProcess?: string;',
    '    jobStart?: string;',
    '    salaryMin?: number;',
    '    salaryMax?: number;',
    "    salaryCurrency?: '€' | '$' | '£' | '¥';",
    '    trialPeriod?: number;',
    '    paymentSchedule?: string;',
    '    notesFromCsm?: string;',
    '};',
].join('\n');

const RECRUITMENT_API_MISSION_RESPONSE_TYPINGS = [
    'type Mission = {',
    '    slug: string;',
    '    status: string | null;',
    '    title: string;',
    '    company: string;',
    '    position: string | null;',
    '    seatsCount: number | null;',
    '    locationType: string | null;',
    '    locations: string[];',
    '    locationCity: string | null;',
    '    languages: string[];',
    '    jobType: string | null;',
    '    jobPresentation: string | null;',
    '    teamPresentation: string | null;',
    '    requirementsDescription: string | null;',
    '    sectorExpMin: number | null;',
    '    sectorExpMax: number | null;',
    '    positionExpMin: number | null;',
    '    positionExpMax: number | null;',
    '    hardSkills: string | null;',
    '    softSkills: string | null;',
    '    possibleEvolutionsDescription: string | null;',
    '    employeeAdvantages: string | null;',
    '    customQuestions: string | null;',
    '    forbiddenHeadhunting: string | null;',
    '    recruitmentProcess: string | null;',
    '    jobStart: string | null;',
    '    salaryMin: number | null;',
    '    salaryMax: number | null;',
    '    salaryCurrency: string | null;',
    '    trialPeriod: number | null;',
    '    paymentSchedule: string | null;',
    '    notesFromCsm: string | null;',
    '};',
    '',
    'type ListMissionsResponse = Mission[];',
].join('\n');

export const RECRUITMENT_API_DOCS_CONTENT = {
    hero: {
        eyebrow: 'Recruitment API',
        title: 'Recruitment API',
        description:
            'Create company records, open recruitment missions, and keep job descriptions synchronized from an external recruitment platform.',
        highlights: [
            { label: 'Auth', value: 'Apikey API key' },
            { label: 'Base path', value: '/partners/v1' },
            { label: 'Companies', value: 'GET + POST /companies' },
            { label: 'Updates', value: 'PATCH /missions/:missionId' },
        ],
    },
    endpoints: [
        {
            method: 'GET',
            path: RECRUITMENT_API_COMPANIES_PATH,
            description: 'List companies owned by the authenticated recruitment partner.',
        },
        {
            method: 'POST',
            path: RECRUITMENT_API_COMPANIES_PATH,
            description: 'Create a partner-owned company and receive its companyId.',
        },
        {
            method: 'GET',
            path: RECRUITMENT_API_MISSIONS_PATH,
            description: 'List missions attached to the authenticated partner companies.',
        },
        {
            method: 'POST',
            path: RECRUITMENT_API_MISSIONS_PATH,
            description: 'Create a private mission for a partner-owned company.',
        },
        {
            method: 'PATCH',
            path: RECRUITMENT_API_MISSION_UPDATE_PATH,
            description: 'Update mission fields, language/location arrays, or live mission status.',
        },
    ] as const satisfies readonly RecruitmentApiDocEndpoint[],
    sections: [
        {
            id: 'overview',
            label: 'Overview & auth',
            eyebrow: 'Start here',
            title: 'Authenticate each request with an API key.',
            summary:
                'The Recruitment API uses the documented Apikey authorization format. Bearer remains accepted by the platform for compatibility.',
            description:
                'Missing or invalid credentials return 401 with a single error key. Every successful read or write is scoped to the authenticated partner account.',
            bullets: [
                'Send Authorization: Apikey <apiKey> on every request.',
                `Use /partners/v1 for new integrations; ${RECRUITMENT_API_LEGACY_BASE_PATH} remains available for HunterOn compatibility.`,
                'Errors use { error: string } so integrations can show the partner-facing message directly.',
                'Company and mission lists only return records owned by the authenticated account.',
            ],
            requestFields: [
                {
                    name: 'Authorization',
                    type: 'Apikey <apiKey>',
                    required: true,
                    location: 'header',
                    description: 'Recruitment API key issued by CrossPath.',
                },
                {
                    name: 'Content-Type',
                    type: 'application/json',
                    required: true,
                    location: 'header',
                    description: 'Required for POST and PATCH request bodies.',
                },
            ],
            responseFields: [
                {
                    name: 'error',
                    type: 'string',
                    required: true,
                    location: 'response',
                    description: 'Returned on 401, 400, 403, and 404 errors.',
                },
            ],
        },
        {
            id: 'companies',
            label: 'Companies',
            eyebrow: 'Company records',
            title: 'Create and list partner-owned companies.',
            summary:
                'Use the companies endpoint to register a company before opening missions against it. The create response returns the company slug as companyId.',
            description:
                'The list response includes only the public company fields documented for recruitment integrations. Internal database fields are not exposed.',
            bullets: [
                'POST accepts name, companySize, websiteUrl, sectors, and presentation fields.',
                'GET returns an array of company objects owned by the authenticated account.',
                'The airtableId response field falls back to the company slug when no Airtable id exists.',
            ],
            method: 'GET + POST',
            path: RECRUITMENT_API_COMPANIES_PATH,
            requestFields: [
                {
                    name: 'Authorization',
                    type: 'Apikey <apiKey>',
                    required: true,
                    location: 'header',
                    description: 'Recruitment API key issued by CrossPath.',
                },
                {
                    name: 'name',
                    type: 'string',
                    required: true,
                    location: 'body',
                    description: 'Company display name.',
                },
                {
                    name: 'companySize',
                    type: 'string',
                    required: false,
                    location: 'body',
                    description: 'Company size label recognized by CrossPath.',
                },
                {
                    name: 'websiteUrl',
                    type: 'url',
                    required: false,
                    location: 'body',
                    description: 'Company website URL.',
                },
                {
                    name: 'sectors',
                    type: 'string[]',
                    required: false,
                    location: 'body',
                    description: 'Sector slugs attached to the company.',
                },
                {
                    name: 'presentation',
                    type: 'string',
                    required: true,
                    location: 'body',
                    description: 'Company overview shown to recruiters.',
                },
                {
                    name: 'valuesPresentation',
                    type: 'string',
                    required: false,
                    location: 'body',
                    description: 'Values and culture overview.',
                },
                {
                    name: 'productPresentation',
                    type: 'string',
                    required: false,
                    location: 'body',
                    description: 'Product or service overview.',
                },
            ],
            responseFields: [
                {
                    name: 'companyId',
                    type: 'string',
                    required: true,
                    location: 'response',
                    description: 'Returned by POST and used as company on mission creation.',
                },
                {
                    name: 'airtableId',
                    type: 'string',
                    required: true,
                    location: 'response',
                    description: 'Returned by GET for each company. Uses the company slug when no Airtable id exists.',
                },
            ],
        },
        {
            id: 'create-mission',
            label: 'Create mission',
            eyebrow: 'Mission intake',
            title: 'Create a private recruitment mission.',
            summary:
                'Create a job description for a company owned by the authenticated account. New missions always start with Private status.',
            description:
                'jobPresentation is required. The API rejects missing or non-owned companies with a documented error payload and stores salary currency internally as ISO.',
            bullets: [
                'company must be a companyId returned by the companies endpoint.',
                'salaryCurrency accepts €, $, £, and ¥ in requests.',
                'Response is { missionId } and list responses expose that id as slug.',
            ],
            method: 'POST',
            path: RECRUITMENT_API_MISSIONS_PATH,
            requestFields: [
                {
                    name: 'company',
                    type: 'string',
                    required: true,
                    location: 'body',
                    description: 'Partner-owned companyId returned by POST /companies.',
                },
                {
                    name: 'title',
                    type: 'string',
                    required: true,
                    location: 'body',
                    description: 'Mission title.',
                },
                {
                    name: 'seatsCount',
                    type: 'number',
                    required: true,
                    location: 'body',
                    description: 'Number of hires needed.',
                },
                {
                    name: 'jobPresentation',
                    type: 'string',
                    required: true,
                    location: 'body',
                    description: 'Role presentation and main responsibilities.',
                },
                {
                    name: 'languages',
                    type: 'string[]',
                    required: false,
                    location: 'body',
                    description: 'Language codes attached to the mission.',
                },
                {
                    name: 'locations',
                    type: 'string[]',
                    required: false,
                    location: 'body',
                    description: 'Location slugs attached to the mission.',
                },
                {
                    name: 'salaryCurrency',
                    type: '€ | $ | £ | ¥',
                    required: false,
                    location: 'body',
                    description: 'Displayed salary currency symbol. CrossPath stores the ISO value internally.',
                },
            ],
            responseFields: [
                {
                    name: 'missionId',
                    type: 'string',
                    required: true,
                    location: 'response',
                    description: 'Created mission id. It is returned as slug in GET /missions.',
                },
            ],
        },
        {
            id: 'list-missions',
            label: 'List missions',
            eyebrow: 'Mission sync',
            title: 'List missions attached to owned companies.',
            summary:
                'Use GET /missions to synchronize the current mission state back into your recruitment system.',
            description:
                'The response includes only documented mission fields, with languages and locations returned as arrays and empty optional arrays returned as [].',
            bullets: [
                'Only missions tied to companies owned by the authenticated account are returned.',
                'Private missions are included so integrations can reconcile newly created jobs.',
                'salaryCurrency is returned as the documented display symbol.',
            ],
            method: 'GET',
            path: RECRUITMENT_API_MISSIONS_PATH,
            requestFields: [
                {
                    name: 'Authorization',
                    type: 'Apikey <apiKey>',
                    required: true,
                    location: 'header',
                    description: 'Recruitment API key issued by CrossPath.',
                },
            ],
            responseFields: [
                {
                    name: 'slug',
                    type: 'string',
                    required: true,
                    location: 'response',
                    description: 'Mission id used by PATCH /missions/:missionId.',
                },
                {
                    name: 'status',
                    type: 'string | null',
                    required: true,
                    location: 'response',
                    description: 'Current mission status.',
                },
                {
                    name: 'languages',
                    type: 'string[]',
                    required: true,
                    location: 'response',
                    description: 'Language codes attached to the mission.',
                },
                {
                    name: 'locations',
                    type: 'string[]',
                    required: true,
                    location: 'response',
                    description: 'Location slugs attached to the mission.',
                },
            ],
        },
        {
            id: 'update-mission',
            label: 'Update mission',
            eyebrow: 'Mission update',
            title: 'Patch mission fields and live statuses.',
            summary:
                'PATCH accepts the documented mission fields as optional values. languages and locations replace the existing arrays only when provided.',
            description:
                'Missions outside the authenticated partner scope return 404. Status can only be updated for missions that are already live, and accepted status values are explicitly limited.',
            bullets: [
                'Allowed statuses: Active, Paused, Closed by CP, Closed by Client.',
                'Updating status while the existing mission is Private returns 403.',
                'Changing company requires the target company to belong to the same authenticated account.',
            ],
            method: 'PATCH',
            path: RECRUITMENT_API_MISSION_UPDATE_PATH,
            requestFields: [
                {
                    name: 'missionId',
                    type: 'string',
                    required: true,
                    location: 'path',
                    description: 'Mission id returned by POST /missions or as slug in GET /missions.',
                },
                {
                    name: 'status',
                    type: 'Active | Paused | Closed by CP | Closed by Client',
                    required: false,
                    location: 'body',
                    description: 'Optional mission status update for non-private missions.',
                },
                {
                    name: 'company',
                    type: 'string',
                    required: false,
                    location: 'body',
                    description: 'Optional replacement companyId owned by the authenticated account.',
                },
                {
                    name: 'languages',
                    type: 'string[]',
                    required: false,
                    location: 'body',
                    description: 'Replaces existing mission languages when present.',
                },
                {
                    name: 'locations',
                    type: 'string[]',
                    required: false,
                    location: 'body',
                    description: 'Replaces existing mission locations when present.',
                },
            ],
            responseFields: [
                {
                    name: 'missionId',
                    type: 'string',
                    required: true,
                    location: 'response',
                    description: 'Updated mission id.',
                },
            ],
        },
    ] as const satisfies readonly RecruitmentApiDocSection[],
    flowSteps: [
        {
            title: 'Create or find the company',
            description: 'POST /companies returns the companyId used for every mission write.',
        },
        {
            title: 'Submit the job description',
            description: 'POST /missions creates a Private mission with jobPresentation and structured salary/location fields.',
        },
        {
            title: 'Synchronize and update',
            description: 'GET /missions lists owned missions, while PATCH updates fields or live statuses.',
        },
    ] as const satisfies readonly RecruitmentApiDocStep[],
    errorExamples: [
        {
            title: 'Missing or invalid API key',
            description: '401 { "error": "Please provide your API key." }',
        },
        {
            title: 'Non-owned company on mission create',
            description: '400 { "error": "..." }',
        },
        {
            title: 'Non-owned mission on update',
            description: '404 { "error": "This mission does not exist or you don\'t have the permission to update it." }',
        },
        {
            title: 'Private mission status update',
            description: '403 { "error": "You can\'t update the status of a JD that is not Live." }',
        },
    ] as const satisfies readonly RecruitmentApiDocStep[],
} as const;

/*----------------------------------
- HELPERS
----------------------------------*/

export const formatRecruitmentApiJson = (value: RecruitmentApiDocJsonValue) => JSON.stringify(value, null, 4);

export const buildRecruitmentApiRequestExample = (
    sectionId: RecruitmentApiDocSectionId,
): RecruitmentApiDocJsonValue => {
    switch (sectionId) {
        case 'companies':
            return { ...RECRUITMENT_API_COMPANY_REQUEST_EXAMPLE };
        case 'create-mission':
            return { ...RECRUITMENT_API_MISSION_REQUEST_EXAMPLE };
        case 'update-mission':
            return { ...RECRUITMENT_API_MISSION_UPDATE_EXAMPLE };
        case 'overview':
        case 'list-missions':
            return {};
    }
};

export const buildRecruitmentApiRequestTypings = (
    sectionId: RecruitmentApiDocSectionId,
): string => {
    switch (sectionId) {
        case 'companies':
            return RECRUITMENT_API_COMPANY_REQUEST_TYPINGS;
        case 'create-mission':
            return RECRUITMENT_API_MISSION_CREATE_REQUEST_TYPINGS;
        case 'update-mission':
            return RECRUITMENT_API_MISSION_UPDATE_REQUEST_TYPINGS;
        case 'overview':
        case 'list-missions':
            return '';
    }
};

export const buildRecruitmentApiResponseExample = (
    sectionId: RecruitmentApiDocSectionId,
): RecruitmentApiDocJsonValue => {
    switch (sectionId) {
        case 'overview':
            return {
                error: 'Please provide your API key.',
            };
        case 'companies':
            return {
                companyId: 'acme-robotics',
            };
        case 'create-mission':
            return {
                missionId: 'rec_01JZ4J6H2T8Y1R3P5N7K9M1Q2W',
            };
        case 'list-missions':
            return [
                {
                    slug: 'rec_01JZ4J6H2T8Y1R3P5N7K9M1Q2W',
                    status: 'Private',
                    title: 'Senior Backend Engineer',
                    company: 'acme-robotics',
                    position: 'backend-engineer',
                    seatsCount: 2,
                    locationType: 'Hybrid',
                    locations: ['france'],
                    locationCity: 'Paris',
                    languages: ['en', 'fr'],
                    jobType: 'CDI',
                    jobPresentation: 'Own API services that coordinate warehouse automation workflows.',
                    teamPresentation: 'Small platform team working with product and robotics engineers.',
                    requirementsDescription: 'Strong TypeScript, distributed systems, and production API experience.',
                    sectorExpMin: 2,
                    sectorExpMax: 6,
                    positionExpMin: 4,
                    positionExpMax: 8,
                    hardSkills: 'TypeScript, Node.js, PostgreSQL, API design',
                    softSkills: 'Clear communication and product judgment',
                    possibleEvolutionsDescription: 'Lead the platform backend scope as the team grows.',
                    employeeAdvantages: 'Hybrid work, hardware lab access, and learning budget.',
                    customQuestions: 'Which API reliability problem are you most proud of solving?',
                    forbiddenHeadhunting: 'Do not contact current Acme Robotics customers.',
                    recruitmentProcess: 'Screening call, technical interview, leadership interview.',
                    jobStart: 'ASAP',
                    salaryMin: 70000,
                    salaryMax: 90000,
                    salaryCurrency: '€',
                    trialPeriod: 3,
                    paymentSchedule: 'Success fee after signed contract.',
                    notesFromCsm: 'Prioritize candidates with logistics or robotics experience.',
                },
            ];
        case 'update-mission':
            return {
                missionId: 'rec_01JZ4J6H2T8Y1R3P5N7K9M1Q2W',
            };
    }
};

export const buildRecruitmentApiResponseTypings = (
    sectionId: RecruitmentApiDocSectionId,
): string => {
    switch (sectionId) {
        case 'overview':
            return [
                'type ErrorResponse = {',
                '    error: string;',
                '};',
            ].join('\n');
        case 'companies':
            return RECRUITMENT_API_COMPANY_RESPONSE_TYPINGS;
        case 'create-mission':
            return [
                'type CreateMissionResponse = {',
                '    missionId: string;',
                '};',
            ].join('\n');
        case 'update-mission':
            return [
                'type UpdateMissionResponse = {',
                '    missionId: string;',
                '};',
            ].join('\n');
        case 'list-missions':
            return RECRUITMENT_API_MISSION_RESPONSE_TYPINGS;
    }
};

export const buildRecruitmentApiAuthSnippet = () => [
    `Authorization: Apikey ${RECRUITMENT_API_KEY_EXAMPLE}`,
    'Content-Type: application/json',
    '',
    '401 response:',
    formatRecruitmentApiJson({ error: 'Please provide your API key.' }),
].join('\n');

export const buildRecruitmentApiCurlSnippet = (
    sectionId: RecruitmentApiDocSectionId,
    baseUrl: string,
) => {
    switch (sectionId) {
        case 'companies':
            return [
                `curl --request POST '${baseUrl}${RECRUITMENT_API_COMPANIES_PATH}' \\`,
                `  --header 'Authorization: Apikey ${RECRUITMENT_API_KEY_EXAMPLE}' \\`,
                "  --header 'Content-Type: application/json' \\",
                `  --data '${JSON.stringify(RECRUITMENT_API_COMPANY_REQUEST_EXAMPLE)}'`,
            ].join('\n');
        case 'create-mission':
            return [
                `curl --request POST '${baseUrl}${RECRUITMENT_API_MISSIONS_PATH}' \\`,
                `  --header 'Authorization: Apikey ${RECRUITMENT_API_KEY_EXAMPLE}' \\`,
                "  --header 'Content-Type: application/json' \\",
                `  --data '${JSON.stringify(RECRUITMENT_API_MISSION_REQUEST_EXAMPLE)}'`,
            ].join('\n');
        case 'list-missions':
            return [
                `curl --request GET '${baseUrl}${RECRUITMENT_API_MISSIONS_PATH}' \\`,
                `  --header 'Authorization: Apikey ${RECRUITMENT_API_KEY_EXAMPLE}'`,
            ].join('\n');
        case 'update-mission':
            return [
                `curl --request PATCH '${baseUrl}${RECRUITMENT_API_MISSION_UPDATE_EXAMPLE_PATH}' \\`,
                `  --header 'Authorization: Apikey ${RECRUITMENT_API_KEY_EXAMPLE}' \\`,
                "  --header 'Content-Type: application/json' \\",
                `  --data '${JSON.stringify(RECRUITMENT_API_MISSION_UPDATE_EXAMPLE)}'`,
            ].join('\n');
        case 'overview':
            return buildRecruitmentApiAuthSnippet();
    }
};

export const buildRecruitmentApiJavaScriptSnippet = (
    sectionId: RecruitmentApiDocSectionId,
    baseUrl: string,
) => {
    switch (sectionId) {
        case 'companies':
            return buildRecruitmentApiFetchSnippet(
                'POST',
                `${baseUrl}${RECRUITMENT_API_COMPANIES_PATH}`,
                RECRUITMENT_API_COMPANY_REQUEST_EXAMPLE,
                'data.companyId',
            );
        case 'create-mission':
            return buildRecruitmentApiFetchSnippet(
                'POST',
                `${baseUrl}${RECRUITMENT_API_MISSIONS_PATH}`,
                RECRUITMENT_API_MISSION_REQUEST_EXAMPLE,
                'data.missionId',
            );
        case 'list-missions':
            return [
                `const response = await fetch('${baseUrl}${RECRUITMENT_API_MISSIONS_PATH}', {`,
                "    method: 'GET',",
                '    headers: {',
                `        Authorization: 'Apikey ${RECRUITMENT_API_KEY_EXAMPLE}',`,
                '    },',
                '});',
                '',
                'const missions = await response.json();',
                'console.log(missions[0]?.slug);',
            ].join('\n');
        case 'update-mission':
            return buildRecruitmentApiFetchSnippet(
                'PATCH',
                `${baseUrl}${RECRUITMENT_API_MISSION_UPDATE_EXAMPLE_PATH}`,
                RECRUITMENT_API_MISSION_UPDATE_EXAMPLE,
                'data.missionId',
            );
        case 'overview':
            return buildRecruitmentApiAuthSnippet();
    }
};

const buildRecruitmentApiFetchSnippet = (
    method: 'POST' | 'PATCH',
    url: string,
    body: RecruitmentApiDocJsonObject,
    logExpression: string,
) => [
    `const response = await fetch('${url}', {`,
    `    method: '${method}',`,
    '    headers: {',
    `        Authorization: 'Apikey ${RECRUITMENT_API_KEY_EXAMPLE}',`,
    "        'Content-Type': 'application/json',",
    '    },',
    `    body: JSON.stringify(${formatRecruitmentApiJson(body)}),`,
    '});',
    '',
    'const data = await response.json();',
    `console.log(${logExpression});`,
].join('\n');
